import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { analyticsReducer, initialState } from '../reducer/analyticsReducer';
import {
	fetchDashboardStart, fetchDashboardSuccess, fetchDashboardError,
	fetchFilteredStart,  fetchFilteredSuccess,  fetchFilteredError,
	fetchHourlyStart,    fetchHourlySuccess,    fetchHourlyError,
	fetchRolesSuccess,   fetchProjectsSuccess,
	setDaysFilter, setTopN,
	setModuleFilter, setUserFilter, setRoleFilter, setProjectFilter,
	setDateFilter, clearFilter, clearDrillDown, clearDateFilter,
	trackAccessSuccess,
} from '../actions/analyticsActions';
import {
	GetDashboardData, GetFilteredData, GetHourlyByDate,
	GetRoles, GetProjects, PostTrackAccess,
} from '../requests/analyticsRequests';

const AnalyticsContext = createContext(null);
const AUTO_REFRESH_MS  = 60_000;

export function AnalyticsProvider({ children }) {
	const [state, dispatch] = useReducer(analyticsReducer, initialState);
	const refreshTimer      = useRef(null);

	// ── core filtered fetch — passes all active filters ───────
	const doFilter = useCallback(async (days, topN, moduleId, userId, roleId, projectName) => {
		dispatch(fetchFilteredStart());
		try {
			const res = await GetFilteredData(days, topN, moduleId, userId, roleId, projectName);
			dispatch(fetchFilteredSuccess(res.data));
		} catch (err) {
			dispatch(fetchFilteredError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, []);

	// ── load roles + projects once on mount ───────────────────
	useEffect(() => {
		GetRoles().then(r => dispatch(fetchRolesSuccess(r.data))).catch(() => {});
		GetProjects().then(r => dispatch(fetchProjectsSuccess(r.data))).catch(() => {});
	}, []);

	// ── load global dashboard ─────────────────────────────────
	const loadDashboard = useCallback(async (days = state.days, topN = state.topN) => {
		dispatch(fetchDashboardStart());
		try {
			const res = await GetDashboardData(days, topN);
			dispatch(fetchDashboardSuccess(res.data));
		} catch (err) {
			dispatch(fetchDashboardError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, [state.days, state.topN]);

	useEffect(() => { loadDashboard(state.days, state.topN); }, [state.days, state.topN]);

	useEffect(() => {
		refreshTimer.current = setInterval(() => loadDashboard(state.days, state.topN), AUTO_REFRESH_MS);
		return () => clearInterval(refreshTimer.current);
	}, [state.days, state.topN]);

	// ── helpers to get current background filters ─────────────
	const getBgFilters = useCallback(() => ({
		roleId:      state.selectedRole?.id    ?? null,
		projectName: state.selectedProject     ?? null,
	}), [state.selectedRole, state.selectedProject]);

	// ── change days ───────────────────────────────────────────
	const changeDays = useCallback((days) => {
		dispatch(setDaysFilter(days));
		if (state.filterType) {
			const { roleId, projectName } = getBgFilters();
			const moduleId = state.filterType === 'module' ? state.selectedModule?.id : null;
			const userId   = state.filterType === 'user'   ? state.selectedUser?.id   : null;
			doFilter(days, state.topN, moduleId, userId, roleId, projectName);
		}
	}, [state, getBgFilters, doFilter]);

	const changeTopN = useCallback((topN) => { dispatch(setTopN(topN)); }, []);

	// ── select role (background filter) ──────────────────────
	const selectRole = useCallback((role) => {
		const isSame = state.selectedRole?.id === role.id;
		const newRole = isSame ? null : role;
		if (isSame && !state.selectedProject) { dispatch(clearFilter()); return; }
		dispatch(isSame ? setRoleFilter(null) : setRoleFilter(role));
		doFilter(state.days, state.topN, null, null, newRole?.id ?? null, state.selectedProject ?? null);
	}, [state.selectedRole, state.selectedProject, state.days, state.topN, doFilter]);

	// ── select project (background filter) ───────────────────
	const selectProject = useCallback((project) => {
		const isSame = state.selectedProject === project;
		const newProject = isSame ? null : project;
		if (isSame && !state.selectedRole) { dispatch(clearFilter()); return; }
		dispatch(setProjectFilter(newProject));
		doFilter(state.days, state.topN, null, null, state.selectedRole?.id ?? null, newProject ?? null);
	}, [state.selectedProject, state.selectedRole, state.days, state.topN, doFilter]);

	// ── select module (drill-down, combines with bg filters) ──
	const selectModule = useCallback((module) => {
		const { roleId, projectName } = getBgFilters();
		if (state.selectedModule?.id === module.id) {
			if (roleId || projectName) {
				dispatch(clearDrillDown());
				doFilter(state.days, state.topN, null, null, roleId, projectName);
			} else {
				dispatch(clearFilter());
			}
			return;
		}
		dispatch(setModuleFilter(module));
		doFilter(state.days, state.topN, module.id, null, roleId, projectName);
	}, [state.selectedModule, state.days, state.topN, getBgFilters, doFilter]);

	// ── select user (drill-down, combines with bg filters) ────
	const selectUser = useCallback((user) => {
		const { roleId, projectName } = getBgFilters();
		if (state.selectedUser?.id === user.id) {
			if (roleId || projectName) {
				dispatch(clearDrillDown());
				doFilter(state.days, state.topN, null, null, roleId, projectName);
			} else {
				dispatch(clearFilter());
			}
			return;
		}
		dispatch(setUserFilter(user));
		doFilter(state.days, state.topN, null, user.id, roleId, projectName);
	}, [state.selectedUser, state.days, state.topN, getBgFilters, doFilter]);

	// ── select date (hourly drill-down) ───────────────────────
	const selectDate = useCallback(async (date) => {
		if (state.selectedDate === date) { dispatch(clearDateFilter()); return; }
		dispatch(setDateFilter(date));
		dispatch(fetchHourlyStart());
		try {
			const moduleId = state.selectedModule?.id  ?? null;
			const userId   = state.selectedUser?.id    ?? null;
			const roleId   = state.selectedRole?.id    ?? null;
			const projectName = state.selectedProject  ?? null;
			const res = await GetHourlyByDate(date, moduleId, userId, roleId, projectName);
			dispatch(fetchHourlySuccess(res.data));
		} catch (err) {
			dispatch(fetchHourlyError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, [state.selectedDate, state.selectedModule, state.selectedUser, state.selectedRole, state.selectedProject]);

	const doClearFilter = useCallback(() => { dispatch(clearFilter()); }, []);
	const doClearDate   = useCallback(() => { dispatch(clearDateFilter()); }, []);

	const trackAccess = useCallback(async (moduleId, moduleName, userId, projectName = null) => {
		try {
			const res = await PostTrackAccess(moduleId, moduleName, userId, projectName);
			dispatch(trackAccessSuccess(res.data));
		} catch { /* fail silently */ }
	}, []);

	return (
		<AnalyticsContext.Provider value={{
			state,
			loadDashboard, changeDays, changeTopN,
			selectModule, selectUser, selectRole, selectProject,
			selectDate,
			clearFilter: doClearFilter,
			clearDate:   doClearDate,
			trackAccess,
		}}>
			{children}
		</AnalyticsContext.Provider>
	);
}

export function useAnalytics() {
	const ctx = useContext(AnalyticsContext);
	if (!ctx) throw new Error('useAnalytics must be used inside <AnalyticsProvider>');
	return ctx;
}